import { Navigate, Route, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import type { ComponentType } from "react";
import RoleRoute from "@/components/RoleRoute";
import { AdminScopeRoute } from "@/components/admin/AdminScopeRoute";
import { AdminDashboardSkeleton } from "@/components/skeletons/AdminDashboardSkeleton";
import { TableSkeleton } from "@/components/skeletons";
import { ADMIN_ROUTE_SCOPES } from "@/lib/admin/adminScope";

const lazyWithRetry = <T extends ComponentType<unknown>>(
  fn: () => Promise<{ default: T }>
) =>
  lazy(() =>
    fn().catch(
      () =>
        new Promise<{ default: T }>((resolve, reject) =>
          setTimeout(() => fn().then(resolve).catch(reject), 1500)
        )
    )
  );

const AdminDashboard = lazyWithRetry(() => import("@/pages/admin/AdminDashboard"));
const PlatformHealthDashboard = lazyWithRetry(() =>
  import("@/pages/admin/PlatformHealthDashboard")
);
const AdminUsersPage = lazyWithRetry(() => import("@/pages/admin/AdminUsersPage"));
const AdminVehiclesPage = lazyWithRetry(() => import("@/pages/admin/AdminVehiclesPage"));
const AdminDocumentsPage = lazyWithRetry(() => import("@/pages/admin/AdminDocumentsPage"));
const AdminKycPage = lazyWithRetry(() => import("@/pages/admin/AdminKycPage"));
const AdminTodayQueuePage = lazyWithRetry(() => import("@/pages/admin/AdminTodayQueuePage"));
const AdminInvoicesPage = lazyWithRetry(() => import("@/pages/admin/AdminInvoicesPage"));
const AdminRevenueDetailsPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminRevenueDetailsPage")
);
const AdminSimulationPage = lazyWithRetry(() => import("@/pages/admin/AdminSimulationPage"));
const RakbNoDepositSimulatorPage = lazyWithRetry(() =>
  import("@/pages/admin/RakbNoDepositSimulatorPage")
);
const AdminWithdrawManagement = lazyWithRetry(() =>
  import("@/pages/admin/withdraws/AdminWithdrawManagement")
);
const AdminPromoCodesPage = lazyWithRetry(() => import("@/pages/admin/AdminPromoCodesPage"));
const AdminCancellationsPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminCancellationsPage")
);
const AdminAgenciesPage = lazyWithRetry(() => import("@/pages/admin/AdminAgenciesPage"));
const AdminAgencyFleetPage = lazyWithRetry(() => import("@/pages/admin/AdminAgencyFleetPage"));
const AdminAddVehiclePage = lazyWithRetry(() => import("@/pages/admin/AdminAddVehiclePage"));
const AdminEditVehiclePage = lazyWithRetry(() => import("@/pages/admin/AdminEditVehiclePage"));
const AdminGuestBookingsPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminGuestBookingsPage")
);
const AdminBookingsPage = lazyWithRetry(() => import("@/pages/admin/AdminBookingsPage"));
const AdminExternalReservationInvoicePage = lazyWithRetry(() =>
  import("@/pages/admin/AdminExternalReservationInvoicePage")
);
const AdminDamageClaimsPage = lazyWithRetry(() => import("@/pages/admin/AdminDamageClaimsPage"));
const AdminChargebacksPage = lazyWithRetry(() => import("@/pages/admin/AdminChargebacksPage"));
const AdminVehicleUpdateRequestsPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminVehicleUpdateRequestsPage")
);
const AdminHealthPage = lazyWithRetry(() => import("@/pages/admin/AdminHealthPage"));
const AdminFleetRemindersPage = lazyWithRetry(() => import("@/pages/admin/AdminFleetRemindersPage"));
const AdminOperationsPage = lazyWithRetry(() => import("@/pages/admin/AdminOperationsPage"));
const AdminAbandonedCheckoutsPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminAbandonedCheckoutsPage")
);
const AdminHotelPartnersPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminHotelPartnersPage")
);
const AdminHotelPartnersFinancePage = lazyWithRetry(() =>
  import("@/pages/admin/AdminHotelPartnersFinancePage")
);
const AdminHotelPartnersImportPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminHotelPartnersImportPage")
);
const AdminHotelPartnerCommissionStatementPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminHotelPartnerCommissionStatementPage")
);
const AdminHotelPartnerDetailsPage = lazyWithRetry(() =>
  import("@/pages/admin/AdminHotelPartnerDetailsPage")
);

/**
 * Admin route definitions as direct <Route> children (used via {AdminRoutes()} in routes.tsx).
 * Page components are lazy-loaded; route config stays in this code-split-friendly module.
 */
function RedirectAdminDemandes() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/admin/documents", search: location.search }} replace />;
}

export function AdminRoutes() {
  return (
    <>
      {/* Routes plus spécifiques avant /admin */}
      <Route
        path="/admin/reminders"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "fleet"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminFleetRemindersPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/health"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "risk"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminHealthPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/platform-health"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "risk"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <PlatformHealthDashboard />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "finance", "risk", "support"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminDashboard />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminUsersPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/vehicles"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "fleet", "va"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminVehiclesPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/demandes"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <RedirectAdminDemandes />
          </RoleRoute>
        }
      />
      <Route
        path="/admin/aujourd-hui"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={ADMIN_ROUTE_SCOPES["/admin/aujourd-hui"]}>
              <Suspense fallback={<TableSkeleton rows={6} columns={4} />}>
                <AdminTodayQueuePage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/kyc"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "va"]}>
              <Suspense fallback={<TableSkeleton rows={6} columns={4} />}>
                <AdminKycPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/documents"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminDocumentsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "finance"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminInvoicesPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={ADMIN_ROUTE_SCOPES["/admin/revenue"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminRevenueDetailsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/simulation"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={ADMIN_ROUTE_SCOPES["/admin/simulation"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminSimulationPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/no-deposit-simulator"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={ADMIN_ROUTE_SCOPES["/admin/no-deposit-simulator"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <RakbNoDepositSimulatorPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/withdraws"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "finance"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminWithdrawManagement />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/promo-codes"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "finance"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminPromoCodesPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/cancellations"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "finance"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminCancellationsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/agencies"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "fleet", "va"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={5} />}>
                <AdminAgenciesPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/agencies/:agencyId/fleet"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "fleet", "va"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={5} />}>
                <AdminAgencyFleetPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/agencies/:agencyId/add-vehicle"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "fleet", "va"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminAddVehiclePage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/agencies/:agencyId/vehicles/:vehicleId/edit"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "fleet", "va"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminEditVehiclePage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/guest-bookings"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={ADMIN_ROUTE_SCOPES["/admin/guest-bookings"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminGuestBookingsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/bookings"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminBookingsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/external-reservations/:id/invoice"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminExternalReservationInvoicePage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/damage-claims"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "risk"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminDamageClaimsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/chargebacks"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "risk"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminChargebacksPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/operations"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "finance", "risk"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminOperationsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/abandoned-checkouts"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={ADMIN_ROUTE_SCOPES["/admin/abandoned-checkouts"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminAbandonedCheckoutsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/hotel-partners"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={ADMIN_ROUTE_SCOPES["/admin/hotel-partners"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminHotelPartnersPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/hotel-partners/import"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "va"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminHotelPartnersImportPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/hotel-partners/finance"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "finance", "va"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminHotelPartnersFinancePage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/hotel-partners/:hotelId/commission-statement"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "finance", "va"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminHotelPartnerCommissionStatementPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/hotel-partners/:hotelId"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "finance", "va"]}>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminHotelPartnerDetailsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/vehicle-update-requests"
        element={
          <RoleRoute allowedRoles={["admin"]}>
            <AdminScopeRoute allowedScopes={["super", "ops", "support", "fleet"]}>
              <Suspense fallback={<TableSkeleton rows={10} columns={6} />}>
                <AdminVehicleUpdateRequestsPage />
              </Suspense>
            </AdminScopeRoute>
          </RoleRoute>
        }
      />
    </>
  );
}
